import clsx, { ClassValue } from 'clsx';
import { createContext, useContext } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export type ControlledFieldProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = { control: Control<T>; field: TName };

// Improve type handling
export function withControlledField<P extends object>(
  Component: React.ComponentType<P>,
) {
  // Return a new component that accepts the same props
  return function WrappedComponent<
    T extends FieldValues = FieldValues,
    TName extends FieldPath<T> = FieldPath<T>,
  >(props: ControlledFieldProps<T, TName> & Omit<P, 'control'>) {
    // Pass the control and field props to the wrapped component
    return <Component {...(props as P)} />;
  };
}

type EnsureKeys<
  ExpectedKeys extends (keyof Target)[],
  Target,
> = keyof Target extends ExpectedKeys[number]
  ? unknown
  : `Missing required keys: ${Exclude<keyof Target, ExpectedKeys[number]> &
      string}`;

/**
 * @link https://github.com/chakra-ui/ark/blob/main/packages/react/src/utils/create-split-props.ts
 */
export function createSplitProps<Target>() {
  return <Keys extends (keyof Target)[], Props extends Target = Target>(
    props: Props,
    keys: Keys & EnsureKeys<Keys, Target>,
  ) =>
    (keys as string[]).reduce<
      [Target, Omit<Props, Extract<(typeof keys)[number], string>>]
    >(
      (previousValue, currentValue) => {
        const [target, source] = previousValue;
        const key = currentValue as keyof Target & keyof typeof source;
        if (source[key] !== undefined) {
          target[key] = source[key];
        }
        delete source[key];
        return [target, source];
      },
      [{} as Target, { ...props }],
    );
}

export function createSafeContext<ContextValue>(errorMessage: string) {
  const Context = createContext<ContextValue | null>(null);

  const useSafeContext = () => {
    const ctx = useContext(Context);

    if (ctx === null) {
      throw new Error(errorMessage);
    }

    return ctx;
  };

  const Provider = ({
    children,
    value,
  }: {
    value: ContextValue;
    children: React.ReactNode;
  }) => <Context.Provider value={value}>{children}</Context.Provider>;

  return [Provider, useSafeContext] as const;
}
